import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Card,
  Container,
  Center,
  Loader,
  Group,
  Pagination,
  ScrollArea,
  Stack,
  Kbd,
  Text,
  Switch,
  Grid,
  Modal,
  TextInput,
  Button,
} from "@mantine/core";
import {
  fetchGallery,
  deleteGalleryImage,
  getActiveJobs,
  getJobResults,
  pollJob,
  fetchBundles,
  createBundle,
  addImagesToBundle,
} from "../api";
import { GalleryImageCard } from "./GalleryImageCard";
import { BundleCard } from "./BundleCard";
import ImageUploader from "../Editor/ImageUploader";
import { useStore } from "../store";
import { useLocation, useSearch } from "wouter";
import SelectionActions from "./SelectionActions";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ImageJobGenerateCard } from "./ImageJobGenerateCard";
import { GalleryVideoCard } from "./GalleryVideoCard";
import GenerateRail from "./GenerateRail";
import { IconPhotoAi } from "@tabler/icons-react";

function SuperGallery() {
  const galleryImages = useStore((state) => state.galleryImages);
  const setGalleryImages = useStore((state) => state.setGalleryImages);
  const jobs = useStore((state) => state.jobs);
  const setJobs = useStore((state) => state.setJobs);

  // Pagination & filter state lives in the URL so back/forward and reloads
  // restore the user's place in the gallery (and so we can deeplink later).
  const search = useSearch();
  const { activePage, userFilter, tagsFilter } = useMemo(() => {
    const params = new URLSearchParams(search);
    const pageRaw = parseInt(params.get("page") ?? "1", 10);
    const tagsRaw = params.get("tags");
    return {
      activePage: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1,
      userFilter: params.get("user") ?? "",
      tagsFilter: tagsRaw ? tagsRaw.split(",").filter(Boolean) : [],
    };
  }, [search]);

  const setImageData = useStore((state) => state.setImageData);
  const [, setLocation] = useLocation();

  const updateQuery = useCallback(
    (updates) => {
      const params = new URLSearchParams(search);
      for (const [key, value] of Object.entries(updates)) {
        const isEmpty =
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) params.delete(key);
        else if (Array.isArray(value)) params.set(key, value.join(","));
        else params.set(key, String(value));
      }
      // Default page=1 stays implicit so the URL stays clean.
      if (params.get("page") === "1") params.delete("page");
      const qs = params.toString();
      setLocation(qs ? `/gallery?${qs}` : "/gallery");
    },
    [search, setLocation]
  );

  const setPage = useCallback((page) => updateQuery({ page }), [updateQuery]);

  // The generation rail is a collapsible panel of this gallery view rather than
  // a separate route. Unset = plan-based default (open for paid users). Once
  // mounted it stays mounted (hidden while collapsed) so in-progress prompt and
  // options survive a collapse.
  const profile = useStore((state) => state.profile);
  const genRailOpen = useStore((state) => state.genRailOpen);
  const setGenRailOpen = useStore((state) => state.setGenRailOpen);
  const railOpen =
    genRailOpen ?? Boolean(profile && profile.plan && profile.plan !== "Free");
  const [railEverMounted, setRailEverMounted] = useState(railOpen);
  useEffect(() => {
    if (railOpen) setRailEverMounted(true);
  }, [railOpen]);

  const [isLoading, setIsLoading] = useState(false);
  const viewportRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 50em)");

  const selectedImages = useStore((state) => state.selectedImages);
  const setSelectedImages = useStore((state) => state.setSelectedImages);

  const showSelectedOnly = useStore((state) => state.showSelectedOnly);
  const setShowSelectedOnly = useStore((state) => state.setShowSelectedOnly);

  // Bundles render alongside ungrouped images on page 1 only; pages 2+ are
  // images-only (a typical user has far fewer bundles than image pages, so
  // mixed pagination would confuse counts). Bundles interleave with images by
  // creation time in the timeline below rather than forming their own block.
  const [bundles, setBundles] = useState([]);
  const [createBundleOpen, setCreateBundleOpen] = useState(false);
  const [newBundleTitle, setNewBundleTitle] = useState("");
  const [creatingBundle, setCreatingBundle] = useState(false);

  const pageSize = 26;


  useEffect(() => {
    setIsLoading(true);
    const galleryPromise = fetchGallery(
      activePage,
      pageSize,
      userFilter,
      tagsFilter
    );
    // Only fetch bundles on page 1 — they only render there.
    const bundlesPromise =
      activePage === 1 ? fetchBundles() : Promise.resolve([]);
    Promise.all([galleryPromise, bundlesPromise])
      .then(([gallery, bundleList]) => {
        setGalleryImages(gallery);
        setBundles(bundleList ?? []);
      })
      .finally(() => setIsLoading(false));
  }, [activePage, userFilter, tagsFilter, setGalleryImages]);

  // Use ref to avoid stale closure in interval / retry handler.
  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // Exposed by the polling effect so the retry button on a job card can
  // re-trigger result fetching without waiting for the next poll tick.
  const retryFinalizeRef = useRef(null);
  const onRetryFinalize = (jobId) => {
    retryFinalizeRef.current?.(jobId);
  };

  // Polling for active jobs.
  //
  // Model: server's /jobs is the source of truth for "is a job still active".
  // When a job disappears from /jobs, we finalize it (fetch results + add to
  // gallery + mark completed). The reconciliation in tick() is the single
  // writer that rebuilds jobs[] from the server response, filtering out any
  // jobId already in the completedJobs LRU so a re-reported job can't reappear.
  useEffect(() => {
    const POLL_INTERVAL_MS = 2500;
    const COMPLETED_CACHE_LIMIT = 200;

    const abortController = new AbortController();
    // jobId -> "completed" | "failed". Bounded LRU by insertion order.
    const completedJobs = new Map();
    // jobIds whose finalize is currently in flight (dedup guard).
    const fetchingResults = new Set();
    let pollInFlight = false;

    const rememberCompleted = (jobId, kind) => {
      completedJobs.set(jobId, kind);
      if (completedJobs.size > COMPLETED_CACHE_LIMIT) {
        const oldest = completedJobs.keys().next().value;
        completedJobs.delete(oldest);
      }
    };

    const isAbortError = (err) =>
      err?.name === "AbortError" || abortController.signal.aborted;

    // Fetch the job's status and (if Success) its results, then add to gallery
    // and mark completed. On result-fetch failure, leave the job in jobs[]
    // with a _resultFetchError marker so the card can show a retry affordance.
    const finalizeJob = async (job) => {
      const jobId = job.jobId;
      if (!jobId) return;
      if (completedJobs.has(jobId)) return; // idempotent
      if (fetchingResults.has(jobId)) return; // dedup concurrent attempts

      fetchingResults.add(jobId);
      // Clear any prior error so the card returns to spinner state.
      setJobs((js) =>
        js.map((j) =>
          j.jobId === jobId ? { ...j, _resultFetchError: undefined } : j
        )
      );

      try {
        const status = await pollJob(jobId, { signal: abortController.signal });
        if (abortController.signal.aborted) return;

        if (status.status === "Success") {
          let results;
          try {
            results = await getJobResults(jobId, {
              signal: abortController.signal,
            });
          } catch (err) {
            if (isAbortError(err)) return;
            console.error("Failed to fetch job results:", err);
            // Keep the job visible with retry UI; do NOT add to completedJobs.
            setJobs((js) =>
              js.map((j) =>
                j.jobId === jobId
                  ? {
                      ...j,
                      _resultFetchError:
                        err?.message || "Couldn't load result",
                    }
                  : j
              )
            );
            return;
          }
          if (abortController.signal.aborted) return;

          rememberCompleted(jobId, "completed");
          setGalleryImages((prev) => {
            const existingIds = new Set(
              prev.data.map((img) => img.logicalImageId)
            );
            const newImages = results.results.filter(
              (img) => !existingIds.has(img.logicalImageId)
            );
            if (newImages.length === 0) return prev;
            return { ...prev, data: [...newImages, ...prev.data] };
          });
          setJobs((js) => js.filter((j) => j.jobId !== jobId));
        } else if (status.status === "Failed") {
          rememberCompleted(jobId, "failed");
          notifications.show({
            title: "Job failed",
            message:
              status.errorMessage ||
              "The job failed to complete. Please try again.",
            color: "red",
          });
          setJobs((js) => js.filter((j) => j.jobId !== jobId));
        }
        // else still Queued/Processing — leave as-is; next poll re-checks.
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("Failed to poll job", jobId, err);
        // Transient; leave the job in state for the next poll to retry.
      } finally {
        fetchingResults.delete(jobId);
      }
    };

    // Expose finalize for the retry button.
    retryFinalizeRef.current = (jobId) => {
      const job = jobsRef.current.find((j) => j.jobId === jobId);
      if (job) finalizeJob(job);
    };

    const tick = async () => {
      if (pollInFlight || abortController.signal.aborted) return;
      pollInFlight = true;
      try {
        const activeJobs = await getActiveJobs({
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;

        const activeIds = new Set(activeJobs.map((j) => j.jobId));
        const localJobs = jobsRef.current;

        // Jobs we have locally that the server no longer reports active.
        const finalizing = localJobs.filter(
          (j) =>
            j.jobId &&
            !activeIds.has(j.jobId) &&
            !completedJobs.has(j.jobId) &&
            !j._resultFetchError // wait for explicit retry
        );

        // Sequential to keep state mutations predictable.
        for (const job of finalizing) {
          if (abortController.signal.aborted) return;
          await finalizeJob(job);
        }

        if (abortController.signal.aborted) return;

        // Single reconciliation: rebuild jobs[] from server's active list
        // (filtering out anything already finalized) plus any local-only
        // entries (optimistic, mid-finalize, or in retry-error state).
        setJobs((current) => {
          const currentMap = new Map(current.map((j) => [j.jobId, j]));
          const merged = activeJobs
            .filter((s) => !completedJobs.has(s.jobId))
            .map((s) => {
              const existing = currentMap.get(s.jobId);
              return existing ? { ...existing, ...s } : s;
            });
          const localOnly = current.filter((j) => {
            if (!j.jobId) return true; // optimistic, no server ack yet
            if (completedJobs.has(j.jobId)) return false;
            if (activeIds.has(j.jobId)) return false; // already in `merged`
            return true; // mid-finalize or in retry-error state
          });
          return [...merged, ...localOnly];
        });
      } catch (err) {
        if (!isAbortError(err)) {
          console.error("Failed to poll active jobs:", err);
        }
      } finally {
        pollInFlight = false;
      }
    };

    tick();
    const intervalId = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      abortController.abort();
      clearInterval(intervalId);
      retryFinalizeRef.current = null;
    };
  }, [setJobs, setGalleryImages]);

  let onDelete = (logicalImageId) => {
    console.debug("onDelete called for image", logicalImageId);
    deleteGalleryImage(logicalImageId).then(() => {
      setGalleryImages({
        ...galleryImages,
        data: galleryImages.data.filter(
          (p) => p.logicalImageId != logicalImageId
        ),
      });
      notifications.show({
        title: "Image deleted",
        message: "The image has been deleted successfully.",
        color: "green",
      });
    });
  };

  let images = galleryImages.data;
  if (showSelectedOnly && selectedImages.length > 0)
    images = galleryImages.data.filter((image) =>
      selectedImages.includes(image.logicalImageId)
    );

  const spans = { base: 6, xs: 6, sm: 4, md: 3, lg: 2, xl: 1 };

  // Bundles (page 1 only) don't render as a hard block above the photos —
  // that backlog of old bundles pushed freshly generated images out of sight.
  // Interleave bundles with loose images on creation time so the gallery reads
  // as one newest-first timeline (matches the server's id-desc ordering).
  const parseTime = (value) => {
    if (!value) return 0;
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  const timeline = [
    ...(bundles ?? []).map((bundle) => ({
      kind: "bundle",
      bundle,
      created: parseTime(bundle.created),
      sortId: bundle.id,
    })),
    ...(images ?? []).map((image) => ({
      kind: "image",
      image,
      created: parseTime(image.created),
      sortId: image.logicalImageId,
    })),
  ].sort((a, b) => {
    if (b.created !== a.created) return b.created - a.created;
    return b.sortId.localeCompare(a.sortId);
  });

  const galleryStack = (
    <Stack
      gap={0}
      style={{
        minHeight:
          "calc(100dvh - var(--app-shell-header-height, 0px) - 2 * var(--app-shell-padding, 0px) - var(--app-shell-footer-height, 0px))",
      }}
    >
        {isLoading ? (
          <Center mt="xl">
            <Loader mt="xl" size="xl" />
          </Center>
        ) : (
          <ScrollArea.Autosize
            style={{
              // must subtract another 28px for the pagination and 8px for Stack-gap
              height:
                "calc(100dvh - var(--app-shell-header-height, 0px) - 2 * var(--app-shell-padding, 0px) - var(--app-shell-footer-height, 0px) - 36px)",
            }}
            viewportRef={viewportRef}
          >
            {/* <SimpleGrid
              type="container"
              cols={{
                base: 1,
                "600px": 2,
                "900px": 3,
                "1200px": 4,
                "1500px": 5,
                "1800px": 6,
              }}
              spacing={{ base: "xs", "600px": "md" }}
            > */}
            <Grid
              p={5}
              gutter={10}
              type="container"
              // gutter={{ base: 5, xs: 'md', md: 'xl', xl: 50 }}
              breakpoints={{
                xs: "580px",
                sm: "860px",
                md: "1200px",
                lg: "1500px",
                xl: "1800px",
              }}
              // style={{
              //   gridTemplateColumns: "repeat(auto-fill, minmax(250px,1fr))",
              // }}
            >
              <Grid.Col span={spans}>
                <Card
                  withBorder
                  shadow="sm"
                  radius="md"
                  style={{ cursor: "pointer" }}
                >
                  <ImageUploader
                    // inline
                    uploadCompleted={(data) => {
                      setGalleryImages((imgs) => {
                        console.debug("Adding image to gallery", imgs, data);
                        return {
                          ...imgs,
                          data: [...data, ...imgs.data],
                        };
                      });
                    }}
                  />
                  <Text mt="xl" ta="center">
                    Pro tip: On keyboard devices, you can also paste images
                    using <Kbd>⌘</Kbd> + <Kbd>V</Kbd> or <Kbd>Ctrl</Kbd> +{" "}
                    <Kbd>V</Kbd>
                  </Text>
                </Card>
              </Grid.Col>

              {jobs?.map((job) => (
                <Grid.Col
                  key={job.jobId ?? job.clientId}
                  span={spans}
                >
                  <ImageJobGenerateCard
                    job={job}
                    onRetry={onRetryFinalize}
                  />
                </Grid.Col>
              ))}

              {timeline.map((item) => {
                if (item.kind === "bundle") {
                  const bundle = item.bundle;
                  return (
                    <Grid.Col key={`bundle-${bundle.id}`} span={spans}>
                      <BundleCard
                        bundle={bundle}
                        onClick={() =>
                          setLocation(`/bundle/${bundle.id}`)
                        }
                      />
                    </Grid.Col>
                  );
                }
                const image = item.image;
                const sharedCardProps = {
                  key: image.logicalImageId,
                  selectionMode: selectedImages.length > 0,
                  onDelete: onDelete,
                  checked: selectedImages.includes(image.logicalImageId),
                  onCheckedChange: (checked) => {
                    if (checked)
                      setSelectedImages([
                        ...selectedImages,
                        image.logicalImageId,
                      ]);
                    else
                      setSelectedImages(
                        selectedImages.filter(
                          (id) => id !== image.logicalImageId
                        )
                      );
                  },
                  onClick: () => {
                    setImageData(image);
                    setLocation(image.videoUrl ? "/video" : "/editor");
                  },
                  image,
                };
                return image.videoUrl ? (
                  <Grid.Col key={image.logicalImageId} span={spans}>
                    <GalleryVideoCard {...sharedCardProps} />
                  </Grid.Col>
                ) : (
                  <Grid.Col key={image.logicalImageId} span={spans}>
                    <GalleryImageCard {...sharedCardProps} />
                  </Grid.Col>
                );
              })}
            </Grid>
          </ScrollArea.Autosize>
        )}
        <div style={{ flex: 1 }}></div>
        <Group p={0} m={0} gap="xs" justify="space-between" wrap="wrap">
          <Pagination
            siblings={isMobile ? 1 : 2}
            boundaries={isMobile ? 0 : 1}
            withControls={!isMobile}
            size="sm"
            m={0}
            value={activePage}
            onChange={setPage}
            total={Math.ceil(galleryImages.totalCount / pageSize)}
          />
          {selectedImages.length > 0 && (
            <SelectionActions
              onDeselect={() => {
                setSelectedImages([]);
                setShowSelectedOnly(false);
              }}
              onCreateBundle={() => {
                setNewBundleTitle("");
                setCreateBundleOpen(true);
              }}
            />
          )}
          <Switch
            label="Show selected only"
            visibleFrom="md"
            disabled={selectedImages.length === 0}
            checked={showSelectedOnly}
            onChange={(event) =>
              setShowSelectedOnly(event.currentTarget.checked)
            }
          />

          {/* <GalleryMenu /> */}
        </Group>
      </Stack>
  );

  return (
    <Container fluid p={0}>
      <Grid gutter={railOpen ? "md" : 0}>
        <Grid.Col
          span={railOpen ? { base: 12, sm: 12, md: 4, lg: 3 } : 12}
          p={0}
          style={{ display: railOpen ? undefined : "none" }}
        >
          {railEverMounted && (
            <GenerateRail onCollapse={() => setGenRailOpen(false)} />
          )}
        </Grid.Col>
        <Grid.Col
          span={railOpen ? { base: 12, sm: 12, md: 8, lg: 9 } : 12}
          p={railOpen ? undefined : 0}
        >
          {!railOpen && (
            <Group p={0} pb="xs">
              <Button
                variant="light"
                size="compact-sm"
                leftSection={<IconPhotoAi size={16} stroke={1.5} />}
                onClick={() => setGenRailOpen(true)}
              >
                Generate
              </Button>
            </Group>
          )}
          {galleryStack}
        </Grid.Col>
      </Grid>

      <Modal
        opened={createBundleOpen}
        onClose={() => !creatingBundle && setCreateBundleOpen(false)}
        title={`Group ${selectedImages.length} ${
          selectedImages.length === 1 ? "image" : "images"
        } into a bundle`}
      >
        <Stack>
          <TextInput
            label="Bundle title"
            placeholder="e.g. CV photos – take 1"
            value={newBundleTitle}
            onChange={(e) => setNewBundleTitle(e.currentTarget.value)}
            data-autofocus
            disabled={creatingBundle}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newBundleTitle.trim() && !creatingBundle)
                onCreateBundleConfirm();
            }}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setCreateBundleOpen(false)}
              disabled={creatingBundle}
            >
              Cancel
            </Button>
            <Button
              loading={creatingBundle}
              disabled={!newBundleTitle.trim()}
              onClick={onCreateBundleConfirm}
            >
              Create bundle
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );

  async function onCreateBundleConfirm() {
    setCreatingBundle(true);
    try {
      const bundle = await createBundle({ title: newBundleTitle.trim() });
      await addImagesToBundle(bundle.id, selectedImages);
      // Refresh both streams: bundles gains an entry, gallery loses the
      // grouped images (they're now hidden by includeBundled=false).
      const [gallery, bundleList] = await Promise.all([
        fetchGallery(activePage, pageSize, userFilter, tagsFilter),
        fetchBundles(),
      ]);
      setGalleryImages(gallery);
      setBundles(bundleList ?? []);
      setSelectedImages([]);
      setShowSelectedOnly(false);
      setCreateBundleOpen(false);
      notifications.show({
        title: "Bundle created",
        message: `Grouped ${selectedImages.length} images into "${bundle.title}"`,
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Couldn't create bundle",
        message: String(err.message ?? err),
        color: "red",
      });
    } finally {
      setCreatingBundle(false);
    }
  }
}

export default SuperGallery;
